import React, { useState } from "react";
import { flushSync } from "react-dom";
import DataTable from "components/DataTable";
import PageWrapperForm from "components/PageWrapperForm";
import DeleteModal from "components/DeleteModal";
import { useTableDataWithPost } from "../../hooks/useTableWithPost";
import { useSession } from "hooks/useSession";

const SecurityBlockManagement = {
  title: "Security Block Management",
  actions: {
    view:   true,
    delete: true,
  },
  columns: [
    { key: "phone",                label: "Phone"      },
    { key: "reason",               label: "Reason"     },
    { key: "blocked_at",           label: "Blocked At" },
    { key: "unblock_at",           label: "Unblock At" },
    { key: "is_currently_blocked", label: "Status"     },
  ],
};

const SecurityBlockManagementPage = () => {
  const [refresh, setRefresh]                   = useState(0);
  const [selectedBlock, setSelectedBlock]       = useState(null);
  const [statusLoading, setStatusLoading]       = useState(false);
  const { userid }                              = useSession();
  const role                                    = localStorage.getItem("role");

  // ── Fetch table data ─────────────────────────────────────────────
  const formdata = new FormData();
  formdata.append("action",    "GET");
  formdata.append("role_name", role);
  formdata.append("userid",    userid);

  const tableData = useTableDataWithPost(
    "https://pro.mokshiq.in/security_block_action",
    SecurityBlockManagement,
    formdata,
    refresh
  );

  // ── onView → open status update modal ───────────────────────────
  const handleView = (block) => {
    flushSync(() => setSelectedBlock(block));
    const modalEl = document.getElementById("updateStatusModal");
    if (modalEl) {
      window.bootstrap.Modal.getOrCreateInstance(modalEl).show();
    }
  };

  // ── Confirm status update (UNBLOCK) ─────────────────────────────
  const confirmStatusUpdate = () => {
    if (!selectedBlock) return;
    setStatusLoading(true);

    const formdata = new FormData();
    formdata.append("action", "UNBLOCK");
    formdata.append("id",     selectedBlock.id);

    fetch("https://pro.mokshiq.in/security_block_action", {
      method: "POST",
      body:   formdata,
    })
      .then((res) => res.json())
      .then((json) => {
        if (json.status === "success") {
          setRefresh((prev) => prev + 1);
          window.bootstrap.Modal
            .getInstance(document.getElementById("updateStatusModal"))
            ?.hide();
        } else {
          alert(json.message || "Status update failed.");
        }
      })
      .catch(() => alert("Something went wrong."))
      .finally(() => setStatusLoading(false));
  };

  // ── Delete ───────────────────────────────────────────────────────
  const handleDelete = (block) => {
    flushSync(() => setSelectedBlock(block));
    const modalEl = document.getElementById("deleteSecurityBlock");
    if (modalEl) {
      window.bootstrap.Modal.getOrCreateInstance(modalEl).show();
    }
  };

  const handleDeleteSuccess = () => {
    setRefresh((prev) => prev + 1);
  };

  return (
    <div>
      <PageWrapperForm config={SecurityBlockManagement}>
        <DataTable
          config={SecurityBlockManagement}
          tableData={tableData}
          onView={handleView}
          onDelete={handleDelete}
          customColumns={{
            is_currently_blocked: (row) =>
              row.is_currently_blocked ? (
                <span className="badge bg-danger">Blocked</span>
              ) : (
                <span className="badge bg-success">Unblocked</span>
              ),
          }}
        />
      </PageWrapperForm>

      {/* ── Status Update Modal ────────────────────────────────────── */}
      <div className="modal fade" id="updateStatusModal" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">

            <div className="modal-header">
              <h5 className="modal-title">
                <i className="iconoir-lock-slash me-2"></i>
                Update Block Status
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                disabled={statusLoading}
              />
            </div>

            <div className="modal-body">
              {selectedBlock && (
                <div>

                  {/* ── User Info ── */}
                  <div className="d-flex align-items-center gap-3 mb-3 p-3 bg-light rounded">
                    <div className="rounded-circle bg-danger d-flex align-items-center justify-content-center"
                      style={{ width: 44, height: 44, minWidth: 44 }}>
                      <i className="iconoir-lock text-white fs-5"></i>
                    </div>
                    <div>
                      <p className="mb-0 fw-semibold">{selectedBlock.phone}</p>
                      <p className="mb-0 text-muted small">{selectedBlock.reason}</p>
                    </div>
                    <div className="ms-auto">
                      {selectedBlock.is_currently_blocked ? (
                        <span className="badge bg-danger">Blocked</span>
                      ) : (
                        <span className="badge bg-success">Unblocked</span>
                      )}
                    </div>
                  </div>

                  {/* ── Block Details ── */}
                  <table className="table table-sm table-borderless mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted small" style={{ width: "40%" }}>Blocked At</td>
                        <td className="small fw-semibold">{selectedBlock.blocked_at}</td>
                      </tr>
                      <tr>
                        <td className="text-muted small">Unblock At</td>
                        <td className="small fw-semibold">{selectedBlock.unblock_at}</td>
                      </tr>
                      <tr>
                        <td className="text-muted small">Reason Code</td>
                        <td className="small fw-semibold">{selectedBlock.reason_code}</td>
                      </tr>
                    </tbody>
                  </table>

                  {/* ── Warning if already unblocked ── */}
                  {!selectedBlock.is_currently_blocked && (
                    <div className="alert alert-success py-2 mt-3 mb-0 small">
                      <i className="iconoir-check-circle me-1"></i>
                      This user is already unblocked.
                    </div>
                  )}

                  {/* ── Confirm message if still blocked ── */}
                  {selectedBlock.is_currently_blocked && (
                    <div className="alert alert-warning py-2 mt-3 mb-0 small">
                      <i className="iconoir-warning-triangle me-1"></i>
                      This will immediately unblock the user and expire the restriction.
                    </div>
                  )}

                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                disabled={statusLoading}
              >
                Close
              </button>

              {/* Only show unblock button if currently blocked */}
              {selectedBlock?.is_currently_blocked && (
                <button
                  className="btn btn-warning"
                  onClick={confirmStatusUpdate}
                  disabled={statusLoading}
                >
                  {statusLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      />
                      Unblocking...
                    </>
                  ) : (
                    <>
                      <i className="iconoir-lock-slash me-1"></i>
                      Unblock User
                    </>
                  )}
                </button>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* ── Delete Modal ───────────────────────────────────────────── */}
      <DeleteModal
        id="deleteSecurityBlock"
        title="Delete Security Block"
        deleteid={selectedBlock?.id}
        message={
          selectedBlock
            ? `Are you sure you want to delete the security block for ${selectedBlock.phone}?`
            : "Are you sure you want to delete this security block record?"
        }
        apiUrl="https://pro.mokshiq.in/security_block_action"
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default SecurityBlockManagementPage;